import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { VIEW_TAB, type PaneId, type PathResolver, type WorkspaceState } from './types';
import {
  focusedPath,
  initialWorkspace,
  sanitizeWorkspace,
  workspaceReducer,
  type WorkspaceAction,
} from './workspaceReducer';
import { WorkspaceContext, type DragInfo, type WorkspaceApi } from './workspaceContext';

/*
  WorkspaceProvider — хозяин вкладок и окон. Источник правды — состояние рабочего пространства,
  адресная строка его отражает: в ней всегда адрес активной вкладки окна в фокусе.

  Откуда приходят изменения:
  - действия пользователя (вкладки, меню, ссылки на объекты) — через API контекста;
  - «назад» и «вперёд» браузера — по событию popstate: адрес открывается как вид или объект;
  - первый заход по ссылке — в начальном состоянии: сохранённые вкладки плюс адрес из ссылки.
  Обычные переходы роутера внутри вкладок не используем: объект открывается через ObjectLink.

  Клавиши (по физическим клавишам, поэтому работают и в русской раскладке):
    Alt+1…9 — вкладка по номеру; Alt+W — закрыть вкладку; Alt+\ — открыть рядом / собрать окна.
*/

interface WorkspaceProviderProps {
  resolve: PathResolver;
  /** Вид по умолчанию — главная. */
  homePath: string;
  /** Ключ хранения вкладок в браузере. */
  storageKey: string;
  children: ReactNode;
}

function load(storageKey: string, homePath: string, resolve: PathResolver, url: string): WorkspaceState {
  let state = initialWorkspace(homePath);
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) state = sanitizeWorkspace(JSON.parse(raw), homePath);
  } catch {
    // Хранилище недоступно или испорчено — начинаем с чистого листа.
  }
  // Ссылка, по которой пришли, важнее сохранённого: открываем её поверх.
  if (url.startsWith(homePath)) {
    const r = resolve(url);
    state =
      r.type === 'view'
        ? workspaceReducer(state, { type: 'setView', path: url })
        : workspaceReducer(state, { type: 'open', path: url, title: r.title, pane: state.focused });
  }
  return state;
}

export function WorkspaceProvider({ resolve, homePath, storageKey, children }: WorkspaceProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(workspaceReducer, undefined, () =>
    load(storageKey, homePath, resolve, window.location.pathname),
  );
  const [dragging, setDragging] = useState<DragInfo | null>(null);
  /** Как отразить следующее изменение в истории: новое открытие — новая запись, переключение — замена. */
  const historyMode = useRef<'push' | 'replace'>('replace');
  const stateRef = useRef(state);

  const act = useCallback((a: WorkspaceAction, mode: 'push' | 'replace' = 'replace') => {
    historyMode.current = mode;
    dispatch(a);
  }, []);

  // Состояние → адресная строка.
  useEffect(() => {
    stateRef.current = state;
    const want = focusedPath(state);
    if (want !== window.location.pathname) {
      navigate(want, { replace: historyMode.current === 'replace' });
    }
    historyMode.current = 'replace';
  }, [state, navigate]);

  // «Назад» и «вперёд»: адрес из истории открываем как вид или объект.
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      if (!path.startsWith(homePath) || path === focusedPath(stateRef.current)) return;
      const r = resolve(path);
      historyMode.current = 'replace';
      dispatch(
        r.type === 'view'
          ? { type: 'setView', path }
          : { type: 'open', path, title: r.title, pane: stateRef.current.focused },
      );
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [homePath, resolve]);

  // Вкладки переживают перезагрузку.
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Приватное окно или запрет хранения — живём без восстановления.
    }
  }, [state, storageKey]);

  // Заголовок окна браузера — полный путь к тому, на что смотрят.
  useEffect(() => {
    const r = resolve(location.pathname);
    document.title = `${r.trail ?? r.title} · Kaname`;
  }, [location.pathname, resolve]);

  const go = useCallback<WorkspaceApi['go']>(
    (path, opts) => {
      const r = resolve(path);
      if (r.type === 'view') {
        act({ type: 'setView', path }, 'push');
        return;
      }
      act(
        { type: 'open', path, title: r.title, pane: opts?.pane, background: opts?.background },
        opts?.background ? 'replace' : 'push',
      );
    },
    [act, resolve],
  );

  const openBeside = useCallback<WorkspaceApi['openBeside']>(
    (path, from = 'main') => {
      const r = resolve(path);
      if (r.type === 'view') {
        go(path);
        return;
      }
      const other: PaneId = from === 'main' ? 'side' : 'main';
      const existing = Object.values(stateRef.current.tabs).find((t) => t.path === path);
      if (existing) act({ type: 'move', id: existing.id, to: other }, 'push');
      else act({ type: 'open', path, title: r.title, pane: other }, 'push');
    },
    [act, go, resolve],
  );

  // Клавиши вкладок и окон.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const s = stateRef.current;
      const pane = s.focused === 'side' && s.side ? s.side : s.main;
      const order = s.focused === 'main' ? [VIEW_TAB, ...pane.tabs] : pane.tabs;
      const digit = /^Digit([1-9])$/.exec(e.code);
      if (digit && !e.shiftKey) {
        const id = order[Number(digit[1]) - 1];
        if (!id) return;
        e.preventDefault();
        act({ type: 'activate', pane: s.focused, id });
      } else if (e.code === 'KeyW' && !e.shiftKey) {
        if (pane.active === VIEW_TAB || !pane.active) return;
        e.preventDefault();
        act({ type: 'close', pane: s.focused, id: pane.active });
      } else if (e.code === 'Backslash') {
        e.preventDefault();
        if (s.side) act({ type: 'unsplit' });
        else if (s.main.active !== VIEW_TAB) act({ type: 'move', id: s.main.active, to: 'side' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act]);

  const api = useMemo<WorkspaceApi>(
    () => ({
      state,
      resolve,
      homePath,
      go,
      openBeside,
      activate: (pane, id) => act({ type: 'activate', pane, id }),
      close: (pane, id) => act({ type: 'close', pane, id }),
      closeOthers: (pane, id) => act({ type: 'closeOthers', pane, id }),
      pin: (id, pinned) => act({ type: 'pin', id, pinned }),
      move: (id, to, index) => act({ type: 'move', id, to, index }),
      unsplit: () => act({ type: 'unsplit' }),
      focus: (pane) => act({ type: 'focus', pane }),
      setSplit: (ratio) => act({ type: 'setSplit', ratio }),
      dragging,
      setDragging,
    }),
    [state, resolve, homePath, go, openBeside, act, dragging],
  );

  return <WorkspaceContext.Provider value={api}>{children}</WorkspaceContext.Provider>;
}
